import { apiFetch } from './http'
import type {
  CohortMetricRow,
  LeagueScope,
  LeagueSetPieces,
  MatchListItem, MatchRanking, MatchReport, MatchSetPieceSheet, MatchSummary,
  PlayerListEntry, PlayerMapSquad, PlayerProfile,
  TeamDossier, TeamListItem, TeamStyleProfile, TeamSetPieces,
} from './types'

// Optional league scope threaded into the team dossier/style endpoints so a
// team can be analysed in a chosen competition/season (league picker).
// `fromDate` (ISO) dokłada okno „od daty", które PRZECHODZI PRZEZ GRANICĘ
// SEZONÓW (styl + predyktory + motoryka w dossier PDF: od 01.01.2026, czyli
// wiosna 25/26 + bieżący sezon). Bez niej endpointy liczą wybrany sezon.
type Scope = { competitionId?: number; seasonId?: number; fromDate?: string }

function scopeQS(opts?: Scope): string {
  const q = new URLSearchParams()
  if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
  if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
  if (opts?.fromDate !== undefined) q.set('from_date', opts.fromDate)
  const qs = q.toString()
  return qs ? `?${qs}` : ''
}

export async function getJSON<T>(path: string): Promise<T> {
  const res = await apiFetch(path, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} on ${path}`)
  return res.json() as Promise<T>
}

export const api = {
  // `includePending` — także mecze rozegrane, których StatsBomb jeszcze nie
  // przetworzył, o ile znamy wynik (forma w dossier). Takie mają has_statsbomb=false.
  matches: (seasonId?: number, teamId?: number, opts?: { includePending?: boolean }) => {
    const q = new URLSearchParams()
    if (seasonId !== undefined) q.set('season_id', String(seasonId))
    if (teamId !== undefined) q.set('team_id', String(teamId))
    if (opts?.includePending) q.set('include_pending', 'true')
    const qs = q.toString()
    return getJSON<MatchListItem[]>(`/api/matches${qs ? `?${qs}` : ''}`)
  },
  // Skład drużyny z sezonu (pozycje, metryczka, G+A, odejścia `left_to`) — kadra
  // na górze dossier i odejścia w tabeli indywidualnej PDF-u; zakres jak dossier.
  playerMapSquad: (teamId: number, opts?: Scope) =>
    getJSON<PlayerMapSquad>(`/api/teams/${teamId}/player-map-squad${scopeQS(opts)}`),
  // `period` (1/2) przelicza Warstwę I na tę połowę (marty *_by_period po stronie
  // backendu); `segment` — na tercję sparingu (zdarzenia w oknie minutowym,
  // training.match_segments); brak obu = cały mecz. Wykluczają się (wygrywa period,
  // ale useMatchTactical nigdy nie poda obu naraz). Zob. MatchReport.period / segment.
  report: (matchId: number, period?: 1 | 2 | null, segment?: number | null) =>
    getJSON<MatchReport>(
      `/api/report/${matchId}${period ? `?period=${period}` : segment ? `?segment=${segment}` : ''}`,
    ),
  pdfUrl: (matchId: number) => `/api/report/${matchId}/pdf`,
  // `source='players'` zawęża listę do zakresów mających marta sezonowego
  // zawodników (kilka lig zamiast ~200 z team_kpi) — picker w „Zawodnikach".
  leagues: (source?: 'teams' | 'players') =>
    getJSON<LeagueScope[]>(`/api/leagues${source ? `?source=${source}` : ''}`),
  teams: (seasonId?: number, competitionId?: number) => {
    const q = new URLSearchParams()
    if (seasonId !== undefined) q.set('season_id', String(seasonId))
    if (competitionId !== undefined) q.set('competition_id', String(competitionId))
    const qs = q.toString()
    return getJSON<TeamListItem[]>(`/api/teams${qs ? `?${qs}` : ''}`)
  },
  teamDossier: (teamId: number, opts?: Scope) =>
    getJSON<TeamDossier>(`/api/teams/${teamId}/dossier${scopeQS(opts)}`),
  dossierPdfUrl: (teamId: number, opts?: Scope) =>
    `/api/teams/${teamId}/dossier/pdf${scopeQS(opts)}`,
  teamStyle: (teamId: number, opts?: Scope) =>
    getJSON<TeamStyleProfile>(`/api/teams/${teamId}/style${scopeQS(opts)}`),
  teamSetPieces: (teamId: number, opts?: Scope) =>
    getJSON<TeamSetPieces>(`/api/teams/${teamId}/set-pieces${scopeQS(opts)}`),
  teamSetPiecesLeague: (teamId: number, opts?: Scope) =>
    getJSON<LeagueSetPieces>(`/api/teams/${teamId}/set-pieces/league${scopeQS(opts)}`),
  // Arkusz SFG jednego meczu (faza × typ) — automatyzacja „SFG STATYSTYKA MECZ".
  // Bez „szansy": tę front dokłada z ręcznego tagowania.
  matchSetPieces: (matchId: number, teamId?: number) =>
    getJSON<MatchSetPieceSheet>(
      `/api/matches/${matchId}/set-pieces${teamId !== undefined ? `?team_id=${teamId}` : ''}`,
    ),
  // Ranking naszych meczów ligowych po kaflach Warstwy I. Bez parametrów —
  // Ekstraklasa w oknie benchmarku raportu pomeczowego (te same percentyle).
  // `competitionId` — ta sama populacja dla INNEJ ligi (tabela „Ranking ligi"
  // w dossier przeciwnika); nasze mecze wracają wtedy puste, wiersze ligi pełne.
  matchRanking: (competitionId?: number) =>
    getJSON<MatchRanking>(`/api/matches/ranking${competitionId !== undefined ? `?competition_id=${competitionId}` : ''}`),
  // Chudy wycinek raportu (skład, strzały, wejścia w PK, pojedynki, dośrodkowania,
  // fazy gry) pod sumy „Podsumowania okresowego" — ~2 s zamiast ~9 s pełnego raportu.
  // `teamId` przestawia perspektywę „my / rywale" na inną drużynę meczu (dossier
  // przeciwnika sumuje jego mecze z jego punktu widzenia); bez = nasza drużyna.
  matchSummary: (matchId: number, teamId?: number) =>
    getJSON<MatchSummary>(`/api/matches/${matchId}/summary${teamId !== undefined ? `?team_id=${teamId}` : ''}`),
  players: (opts?: {
    seasonId?: number
    competitionId?: number
    positionLine?: string
    positionGroup?: string
  }) => {
    const q = new URLSearchParams()
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.positionLine) q.set('position_line', opts.positionLine)
    if (opts?.positionGroup) q.set('position_group', opts.positionGroup)
    const qs = q.toString()
    return getJSON<PlayerListEntry[]>(`/api/players${qs ? `?${qs}` : ''}`)
  },
  playerCohort: (positionGroup: string, opts?: { competitionId?: number; seasonId?: number }) => {
    const q = new URLSearchParams()
    q.set('position_group', positionGroup)
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    return getJSON<CohortMetricRow[]>(`/api/players/cohort?${q.toString()}`)
  },
  playerProfile: (playerId: number, pos?: string, opts?: Scope) => {
    const q = new URLSearchParams()
    if (pos) q.set('pos', pos)
    if (opts?.competitionId !== undefined) q.set('competition_id', String(opts.competitionId))
    if (opts?.seasonId !== undefined) q.set('season_id', String(opts.seasonId))
    const qs = q.toString()
    return getJSON<PlayerProfile>(`/api/players/${playerId}${qs ? `?${qs}` : ''}`)
  },
}
